import { motion } from 'framer-motion';
import { Car, Droplets, Sparkles, Wind, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';

const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';
const SPRAY_WASH_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/tf446cm4_480579292_1087604693169911_8215014255740035868_n.jpg';
const HAND_WASH_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/swslw90w_480790144_1087613703169010_4496990203856420121_n.jpg';
const EXTERIOR_IMG = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/eif4mi42_474949816_1065542595376121_1473048260461701712_n.jpg';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const ServicesPage = () => {
  const services = [
    {
      id: 'fullservice',
      icon: Sparkles,
      title: 'Full-Service Wash Experience',
      img: HAND_WASH_IMG,
      description: 'Our signature service combines the best of both worlds. First, our trained staff gives your vehicle personal hand-wash attention, then we send you through our automated tunnel for a perfect finish.',
      features: [
        'Hand prep and scrub by trained staff',
        'Pre-soak and bug removal treatment',
        'Wheel and tire hand cleaning',
        'Then through our automated tunnel',
        'Soft-touch brushes & triple foam',
        'Spot-free rinse and powerful dryers'
      ],
      note: 'The complete wash experience - hand attention plus tunnel technology.'
    },
    {
      id: 'tunnel',
      icon: Car,
      title: 'Express Tunnel Wash',
      img: SPRAY_WASH_IMG,
      description: 'Need a quick wash? Our state-of-the-art tunnel uses Sonny\'s vehicle washing systems with Ryko SoftGloss and FoamBrite material for a fast, thorough clean.',
      features: [
        'Soft-touch brushes for safe cleaning',
        'Triple foam polish application',
        'High-pressure undercarriage wash',
        'Spot-free rinse system',
        'Powerful air dryers',
        'Quick cycle time'
      ],
      note: 'Perfect for regular maintenance washes when you\'re short on time.'
    },
    {
      id: 'selfservice',
      icon: Droplets,
      title: 'Self-Service Bays',
      img: EXTERIOR_IMG,
      description: 'Take your time with our self-service wash bays. Professional-grade equipment lets you wash your way.',
      features: [
        'High-pressure wash wand',
        'Foam brush for gentle scrubbing',
        'Tire and engine cleaner',
        'Rinse and spot-free rinse options',
        'Well-lit, covered bays',
        'Great for trucks and oversized vehicles'
      ],
      note: 'Perfect for DIYers and vehicles that need special attention.'
    },
    {
      id: 'vacuum',
      icon: Wind,
      title: 'Quarter Vacuums',
      img: null,
      description: 'Keep your interior as clean as your exterior. Our quarter vacuums ($0.25) have powerful suction that really gets the job done.',
      features: [
        'Powerful suction',
        'Long hoses reach everywhere',
        'Multiple vacuum stations',
        'Available during all business hours',
        'Perfect for quick cleanups',
        'Only $0.25 per use'
      ],
      note: 'Quarter vacuums available during all business hours.'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <PageHero 
        badge="Our Services"
        title="Complete"
        titleAccent="Car Care"
        subtitle="The only full-service car wash in Tahlequah combining hand washing with our automated tunnel."
      />

      {/* Services List */}
      <section className="bg-white py-20 md:py-32" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              {...fadeInUp}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 last:mb-0 ${
                idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`service-${service.id}`}
            >
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#1E3A8A] rounded-xl flex items-center justify-center">
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A]">{service.title}</h2>
                </div>
                
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-sm text-slate-500 italic mb-6">{service.note}</p>
              </div>

              {service.img ? (
                <div className={`relative ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className={`absolute -top-4 ${idx % 2 === 1 ? '-right-4' : '-left-4'} w-full h-full bg-red-500 rounded-2xl`} />
                  <img 
                    src={service.img} 
                    alt={service.title}
                    className="relative z-10 w-full h-[350px] object-cover rounded-2xl shadow-xl"
                  />
                </div>
              ) : (
                <div className={`relative ${idx % 2 === 1 ? 'lg:order-1' : ''} bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] rounded-2xl p-12 flex items-center justify-center h-[350px]`}>
                  <div className="text-center">
                    <service.icon className="w-24 h-24 text-white/20 mx-auto mb-4" />
                    <p className="text-white text-2xl font-bold">Just $0.25 per use!</p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Detailing Teaser */}
      <section className="bg-slate-50 py-20 md:py-32" data-testid="services-detailing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Premium Service
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              Vehicle Detailing
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Looking for a deeper clean? We offer professional detailing services by appointment. From mini details to full interior and exterior packages, we'll make your vehicle look like new.
            </p>
            <Link to="/contact" data-testid="services-detailing-contact-btn">
              <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white font-bold px-8 py-3 rounded-full btn-secondary">
                Contact Us for Detailing
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Hours Section */}
      <section className="bg-white py-20 md:py-32" data-testid="services-hours">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="bg-[#0F172A] rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <motion.div 
              className="absolute inset-0 opacity-20"
              style={{
                background: 'radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)'
              }}
            />
            <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-8 h-8 text-red-500" />
                  <h3 className="text-2xl font-bold text-white">Service Hours</h3>
                </div>
                <p className="text-slate-300 mb-4">We're open 7 days a week for your convenience.</p>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-white font-bold text-xl">9 AM - 7 PM Daily</p>
                  <p className="text-slate-400 text-sm mt-1">Including weekends & holidays</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-slate-300 mb-4">Save on every wash with</p>
                <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="services-everwash-cta">
                  <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-8 py-3 rounded-full btn-primary">
                    Join EverWash
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#DC2626] py-20 md:py-24 relative overflow-hidden" data-testid="services-cta">
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
          animate={{ x: [0, 60], y: [0, 60] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Ready for a Clean Car?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Stop by today or join EverWash for unlimited washes. Either way, we've got you covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="services-final-everwash-cta">
                <Button className="w-full sm:w-auto bg-white text-[#DC2626] hover:bg-slate-100 font-bold text-lg px-10 py-6 rounded-full">
                  Join EverWash
                </Button>
              </a>
              <a 
                href="https://maps.app.goo.gl/pzyefhoDAQCvaCp89"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-directions-btn"
              >
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 font-bold text-lg px-10 py-6 rounded-full">
                  Get Directions
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
